import { jwtDecode } from "jwt-decode";
import { MAINTENANCE_USER_TYPE } from "../../useCurrentUser";
import { fetchSessionWithToken } from "./maintenanceApi";
import { MaintSession } from "./types";

export const USER_TYPE_CLAIM = "https://paperlessnow.ca/userType";

/** The role claim the Auth0 post-login Action puts on the access token, or null. */
export const userTypeFromToken = (accessToken: string): number | null => {
  try {
    const claims: any = jwtDecode(accessToken);
    const raw = claims ? claims[USER_TYPE_CLAIM] : undefined;
    const value = typeof raw === "string" && /^\d+$/.test(raw.trim()) ? Number(raw.trim()) : raw;
    return typeof value === "number" && Number.isInteger(value) ? value : null;
  } catch (_) {
    return null;
  }
};

export interface SignInDeps {
  getAccessTokenSilently: () => Promise<string>;
  getIdTokenClaims: () => Promise<any>;
  user: { email?: string; name?: string; picture?: string } | undefined;
  fetchSession?: (accessToken: string) => Promise<MaintSession>;
  store?: (key: string, value: string) => void;
  clear?: () => void;
  go?: (path: string) => void;
  logout?: () => void;
}

/**
 * The sign-in bootstrap for a maintenance-only account (role 11), chosen by the
 * role claim on its access token. It asks GET /maintenance/session (who am I,
 * from the verified token) instead of POST /user (which trusts a posted email).
 *
 * Returns false for every other role, and whenever the role can't be read, so
 * the existing sign-in continues exactly as before: HR and the bike tablet are
 * never routed through here.
 */
export const maintenanceSignIn = async (deps: SignInDeps): Promise<boolean> => {
  let accessToken: string;
  try {
    accessToken = await deps.getAccessTokenSilently();
  } catch (_) {
    return false;
  }
  if (userTypeFromToken(accessToken) !== MAINTENANCE_USER_TYPE) return false;

  const store = deps.store || ((key: string, value: string) => localStorage.setItem(key, value));
  const clear = deps.clear || (() => localStorage.clear());
  const go = deps.go || ((path: string) => {
    window.location.href = window.location.origin + path;
  });
  try {
    const session = await (deps.fetchSession || fetchSessionWithToken)(accessToken);
    if (!session || !session.user || !session.user.user_id) throw new Error("no account");
    const claims = await deps.getIdTokenClaims();
    const profile = deps.user || {};
    store(
      "userInfo",
      JSON.stringify({
        ...session.user,
        name: profile.name || session.display_name || session.user.email,
        email: profile.email || session.user.email,
        picture: profile.picture,
        idToken: claims && claims.__raw,
        accessToken,
      })
    );
    store("expiresAt", JSON.stringify(Number(claims && claims.exp) * 1000));
    go("/maintenance");
  } catch (_) {
    // No account behind this sign-in (or the session can't be read): same as the
    // existing flow when POST /user finds no user_id.
    clear();
    if (deps.logout) deps.logout();
  }
  return true;
};
